import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { api, imgUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useQueryClient } from "@tanstack/react-query";
import { FREE_PERIOD } from "@/config";
import { Button } from "@/components/ui/button";
import { ReportDialog } from "@/components/ReportDialog";
import { MediaPermissionDialog, PartnerMediaBanner } from "@/components/MediaPermissionDialog";
import { NetworkCheck } from "@/components/NetworkCheck";
import { dailyCallOptions, savedMediaConstraints, topLayer, DEVICE_KEYS, applySavedDevices } from "@/lib/media";
import { VerifiedBadge } from "@/components/VerifiedBadge";
import { PremiumBadge } from "@/components/PremiumBadge";
import { RateCallCard } from "@/components/RateCallCard";
import { openUpgradeSheet } from "@/components/UpgradeSheet";
import { Mic, MicOff, Video, VideoOff, PhoneOff, Flag, Heart, X, Sparkles, MessageCircle, RotateCcw, Crown, ThumbsUp, ThumbsDown, AudioLines, WifiOff } from "lucide-react";
import { toast } from "sonner";

const DURATION = 60;

export default function CallRoom() {
  const { matchId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const qc = useQueryClient();
  const [params] = useSearchParams();
  const bonus = params.get("bonus") === "1";
  const autostart = params.get("autostart") === "1";
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const endTimeRef = useRef(null);
  const audioCtxRef = useRef(null);
  const rafRef = useRef(null);
  const dailyRef = useRef(null);
  const dailyMode = useRef(false);
  const remoteVideoRef = useRef(null);
  const localVideoRef = useRef(null);
  const [remoteP, setRemoteP] = useState(null);
  const [localP, setLocalP] = useState(null);

  const [phase, setPhase] = useState("loading"); // loading | lobby | countdown | incall | deciding | confirmed | closed
  const [, setSkipTick] = useState(0);
  const [partner, setPartner] = useState(null);
  const partnerRef = useRef(null);
  useEffect(() => { partnerRef.current = partner; }, [partner]);
  const [event, setEvent] = useState(null);
  const [remaining, setRemaining] = useState(DURATION);
  const [duration, setDuration] = useState(DURATION);
  const [preCount, setPreCount] = useState(3);
  const [micLevel, setMicLevel] = useState(0);
  const [micOn, setMicOn] = useState(true);
  const [camOn, setCamOn] = useState(true);
  const [camError, setCamError] = useState(false);
  const [mediaError, setMediaError] = useState(null);
  const [remoteMuted, setRemoteMuted] = useState(false);
  const [waitingStart, setWaitingStart] = useState(false);
  const [netState, setNetState] = useState("good");
  const [paused, setPaused] = useState(false);
  const [pauseLeft, setPauseLeft] = useState(null);
  const [selfReconnecting, setSelfReconnecting] = useState(false);
  const [audioOnly, setAudioOnly] = useState(false);
  const [voicePrompt, setVoicePrompt] = useState(false);
  const [rateOpen, setRateOpen] = useState(false);
  const [inviteStatus, setInviteStatus] = useState(null);
  const pausedRef = useRef(false);
  const leavingRef = useRef(false);
  const audioOnlyRef = useRef(false);
  const promptedRef = useRef(false);
  const reconnectsRef = useRef(0);
  const worstNetRef = useRef("good");
  const afterRateRef = useRef(null);
  const [deciding, setDeciding] = useState(false);
  const [rematchInfo, setRematchInfo] = useState({ count: 0, max: 2 });
  const [nudgeIncoming, setNudgeIncoming] = useState(false);
  const [needsDecision, setNeedsDecision] = useState(true);
  const canRematch = rematchInfo.count < rematchInfo.max;

  const stopStream = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (audioCtxRef.current) { audioCtxRef.current.close().catch(() => {}); audioCtxRef.current = null; }
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setMicLevel(0);
    const call = dailyRef.current;
    if (call) {
      leavingRef.current = true;
      dailyRef.current = null;
      call.leave().catch(() => {});
      try { call.destroy(); } catch {}
      setRemoteP(null); setLocalP(null); dailyMode.current = false;
    }
  }, []);

  const joinDaily = useCallback(async (roomUrl, token) => {
    const Daily = (await import("@daily-co/daily-js")).default;
    const existing = Daily.getCallInstance?.();
    if (existing) { try { await existing.destroy(); } catch {} }
    const call = Daily.createCallObject(dailyCallOptions());
    dailyRef.current = call;
    leavingRef.current = false;
    const refresh = () => {
      if (dailyRef.current !== call) return;
      const ps = call.participants();
      setLocalP(ps.local ? { ...ps.local } : null);
      const remote = Object.values(ps).find((p) => !p.local);
      setRemoteP(remote ? { ...remote, tracks: { ...remote.tracks } } : null);
      const lb = ps.local?.tracks?.video?.blocked || ps.local?.tracks?.audio?.blocked;
      if (lb?.byPermissions) setMediaError("permissions");
      else if (lb?.byDeviceMissing) setMediaError("not-found");
      else if (lb?.byDeviceInUse) setMediaError("in-use");
    };
    ["participant-joined", "participant-updated", "participant-left", "track-started", "track-stopped", "joined-meeting", "left-meeting"]
      .forEach((ev) => call.on(ev, refresh));
    call.on("participant-joined", (e) => {
      if (e?.participant?.local) return;
      toast.success(`${e?.participant?.user_name || "Your match"} joined the vibe check`);
      if (audioOnlyRef.current) call.updateParticipant(e.participant.session_id, { setSubscribedTracks: { audio: true, video: false } });
    });
    // Auto-reconnect: Daily retries ICE itself; if we get dropped from the room, rejoin with the same token
    call.on("network-connection", (e) => {
      if (e?.event === "interrupted") setSelfReconnecting(true);
      else if (e?.event === "connected") setSelfReconnecting(false);
    });
    call.on("left-meeting", () => {
      if (leavingRef.current || dailyRef.current !== call) return;
      setSelfReconnecting(true);
      let tries = 0;
      const retry = () => {
        if (leavingRef.current || dailyRef.current !== call || tries++ >= 6) return;
        call.join({ url: roomUrl, token }).then(() => setSelfReconnecting(false)).catch(() => setTimeout(retry, 2500));
      };
      setTimeout(retry, 1500);
    });
    call.on("camera-error", (e) => {
      setCamError(true);
      const t = e?.error?.type;
      setMediaError(t === "permissions" ? "permissions" : t === "not-found" ? "not-found" : t === "cam-in-use" || t === "mic-in-use" || t === "cam-mic-in-use" ? "in-use" : "unknown");
    });
    call.on("error", (e) => toast.error(e?.errorMsg || "Video connection error"));
    // Adaptive quality: drop to the low layer (320x240 @15fps) when the network degrades so audio stays clear
    let layer = null;
    call.on("network-quality-change", (e) => {
      const state = e?.networkState || (e?.threshold === "good" ? "good" : e?.threshold === "low" ? "warning" : "bad");
      setNetState(state);
      const rank = { good: 0, warning: 1, bad: 2 };
      if ((rank[state] ?? 0) > (rank[worstNetRef.current] ?? 0)) worstNetRef.current = state;
      const next = state === "good" ? topLayer() : state === "warning" ? 1 : 0;
      if (next === layer) return;
      layer = next;
      call.updateReceiveSettings({ "*": { video: { layer: next } } }).catch(() => {});
      call.updateSendSettings({ video: { maxQuality: state === "bad" ? "low" : state === "warning" ? "medium" : "high" } }).catch(() => {});
    });
    try {
      await call.join({ url: roomUrl, token });
      applySavedDevices(call);
      call.getNetworkTopology().then((t) => { if (t?.topology === "peer") call.setNetworkTopology({ topology: "sfu" }).catch(() => {}); }).catch(() => {});
      refresh();
    } catch (e) {
      toast.error("Couldn't connect to the video room — please retry");
    }
  }, []);

  const retryMedia = async () => {
    setMediaError(null);
    const call = dailyRef.current;
    if (call) {
      try { await call.setInputDevicesAsync({ videoSource: true, audioSource: true }); } catch {}
      call.setLocalVideo(true); call.setLocalAudio(true);
      setCamOn(true); setMicOn(true); setCamError(false);
    } else {
      streamRef.current?.getTracks().forEach((t) => t.stop()); streamRef.current = null;
      startCamera();
    }
  };

  const startCamera = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia(await savedMediaConstraints());
      streamRef.current = stream;
      if (videoRef.current) videoRef.current.srcObject = stream;
      setCamError(false);
      try {
        const AC = window.AudioContext || window.webkitAudioContext;
        const ctx = new AC(); audioCtxRef.current = ctx;
        const src = ctx.createMediaStreamSource(stream);
        const analyser = ctx.createAnalyser(); analyser.fftSize = 256;
        src.connect(analyser);
        const buf = new Uint8Array(analyser.frequencyBinCount);
        const loop = () => {
          analyser.getByteFrequencyData(buf);
          const avg = buf.reduce((a, b) => a + b, 0) / buf.length;
          setMicLevel(Math.min(1, avg / 80));
          rafRef.current = requestAnimationFrame(loop);
        };
        loop();
      } catch {}
    } catch (e) {
      setCamError(true);
      setMediaError(e?.name === "NotAllowedError" || e?.name === "SecurityError" ? "permissions"
        : e?.name === "NotFoundError" ? "not-found" : e?.name === "NotReadableError" ? "in-use" : "unknown");
    }
  }, []);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const m = (await api.get(`/matches/${matchId}`)).data;
        if (!mounted) return;
        setPartner(m.partner); setEvent(m.event);
        setDuration(m.call_duration ?? DURATION); setRemaining(m.call_duration ?? DURATION);
        setRematchInfo({ count: m.rematch_count ?? 0, max: m.rematch_max ?? 2 });
        setNudgeIncoming(!!m.nudge_incoming);
        setNeedsDecision(m.status !== "confirmed");
        const ready = autostart ? "countdown" : "lobby";
        const gate = async () => {
          // Daily cap check BEFORE the camera loads
          const usage = (await api.get("/usage")).data;
          const counts = m.status !== "confirmed" || !usage.trial_active;
          const sessionCounted = (m.status === "confirmed" ? m.bonus_call_started_at : m.call_started_at)
            && (m[m.status === "confirmed" ? "bonus_counted" : "counted"] || []).includes(user?.user_id);
          if (!usage.premium && !sessionCounted && usage.vibe_tokens <= 0 && (usage.locked || (counts && usage.calls_blocked))) {
            setPhase("limit");
            openUpgradeSheet({ reason: "calls", message: usage.limit_message, usage });
            return;
          }
          setPhase(ready);
        };
        if (m.status === "confirmed") { if (bonus) await gate(); else setPhase("confirmed"); return; }
        if (m.nudge_incoming) { setPhase("nudge"); return; }
        if (m.status === "closed") { setPhase("closed"); return; }
        if (m.my_decision) { setPhase("deciding"); return; }
        await gate();
      } catch { navigate("/matches"); }
    })();
    return () => { mounted = false; stopStream(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchId, navigate, stopStream]);

  // Start camera preview when entering the lobby (or simulated in-call)
  useEffect(() => {
    if ((phase === "lobby" || (phase === "incall" && !dailyMode.current)) && !streamRef.current) startCamera();
  }, [phase, startCamera]);

  // Attach Daily local/remote media tracks to their video elements
  useEffect(() => {
    const t = localP?.tracks?.video?.persistentTrack;
    const el = localVideoRef.current;
    if (!el) return;
    const cur = el.srcObject?.getVideoTracks?.()[0];
    if (cur?.id !== t?.id) el.srcObject = t ? new MediaStream([t]) : null;
  }, [localP, phase]);
  useEffect(() => {
    const el = remoteVideoRef.current;
    if (!el) return;
    const vt = remoteP?.tracks?.video?.persistentTrack;
    const at = remoteP?.tracks?.audio?.persistentTrack;
    const tracks = [vt, at].filter(Boolean);
    const ids = tracks.map((t) => t.id).sort().join();
    const curIds = el.srcObject ? el.srcObject.getTracks().map((t) => t.id).sort().join() : "";
    if (ids !== curIds) el.srcObject = tracks.length ? new MediaStream(tracks) : null;
    el.volume = 1;
    if (tracks.length && el.paused) el.play().catch(() => { el.muted = true; setRemoteMuted(true); el.play().catch(() => {}); });
  }, [remoteP, phase]);

  const unmuteRemote = () => {
    const el = remoteVideoRef.current;
    if (el) { el.muted = false; el.play().catch(() => {}); }
    setRemoteMuted(false);
  };

  // Keep the video element bound to the stream across phase changes
  useEffect(() => {
    if (videoRef.current && streamRef.current) videoRef.current.srcObject = streamRef.current;
  }, [phase]);

  // 3-2-1 pre-call countdown
  useEffect(() => {
    if (phase !== "countdown") return;
    setPreCount(3);
    const iv = setInterval(() => {
      setPreCount((c) => {
        if (c <= 1) { clearInterval(iv); beginCall(); return 0; }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  const beginCall = async () => {
    try {
      const { data } = await api.post(`/matches/${matchId}/call/start`);
      qc.invalidateQueries({ queryKey: ["usage"] });
      if (data.durationSeconds) setDuration(data.durationSeconds);
      endTimeRef.current = data.started ? Date.now() + data.remaining * 1000 : null;
      setWaitingStart(!data.started);
      setRemaining(data.remaining);
      if (data.mode === "daily" && data.roomUrl && data.token) {
        dailyMode.current = true;
        if (streamRef.current) { streamRef.current.getTracks().forEach((t) => t.stop()); streamRef.current = null; }
        await joinDaily(data.roomUrl, data.token);
      } else {
        dailyMode.current = false;
        if (!streamRef.current) await startCamera();
      }
    } catch (e) {
      if (e?.response?.status === 402) {
        stopStream();
        setPhase("limit");
        return;
      }
      endTimeRef.current = Date.now() + duration * 1000;
      setRemaining(duration);
    }
    setPhase("incall");
  };

  // Server is the source of truth for the shared timer (joint start, pauses, reconnect grace)
  const applySync = useCallback((data) => {
    if (!data?.started) return;
    setWaitingStart(false);
    pausedRef.current = !!data.paused;
    setPaused(!!data.paused);
    setPauseLeft(data.pause_left ?? null);
    if (data.remaining <= 0) { pausedRef.current = false; endTimeRef.current = Date.now(); return; }
    if (!data.paused) {
      const target = Date.now() + data.remaining * 1000;
      if (!endTimeRef.current || Math.abs(endTimeRef.current - target) > 1500) endTimeRef.current = target;
    }
  }, []);

  const syncPresence = useCallback(async (present = true) => {
    try {
      const { data } = await api.post(`/matches/${matchId}/call/sync`, { present });
      if (present) applySync(data);
      if (present && !data.started && data.invite_status) {
        setInviteStatus(data.invite_status);
        if (data.invite_status === "declined") {
          toast(`${partnerRef.current?.name || "Your match"} declined the vibe check`);
          stopStream();
          navigate("/discover");
        }
      }
    } catch {}
  }, [matchId, applySync, navigate, stopStream]);

  useEffect(() => {
    if (phase !== "incall") return;
    syncPresence(true);
    const iv = setInterval(() => syncPresence(true), 1500);
    return () => clearInterval(iv);
  }, [phase, syncPresence, remoteP?.session_id]);

  useEffect(() => () => { if (!endTimeRef.current) api.post(`/matches/${matchId}/call/sync`, { present: false }).catch(() => {}); }, [matchId]);

  // Pause the shared timer while someone's connection is down (debounced 2s), resume when they're back
  const partnerDropped = phase === "incall" && dailyMode.current && !waitingStart && !partner?.is_demo
    && (!remoteP || remoteP?.tracks?.audio?.state === "interrupted");
  const dropped = partnerDropped || (phase === "incall" && selfReconnecting);
  const droppedRef = useRef(false);
  useEffect(() => {
    if (phase !== "incall" || waitingStart || !endTimeRef.current) return;
    const send = (val) => {
      if (droppedRef.current === val) return;
      droppedRef.current = val;
      if (val) reconnectsRef.current += 1;
      api.post(`/matches/${matchId}/call/pause`, { paused: val }).then(({ data }) => applySync(data)).catch(() => {});
    };
    if (!dropped) { send(false); return; }
    const t = setTimeout(() => send(true), 2000);
    return () => clearTimeout(t);
  }, [dropped, phase, waitingStart, matchId, applySync]);

  const finishCall = useCallback(() => {
    const hadCall = !!endTimeRef.current;
    stopStream();
    api.get(`/matches/${matchId}/call`).catch(() => {});
    if (hadCall) setRateOpen(true);
    if (user?.premium || FREE_PERIOD) {
      if (needsDecision) setPhase("deciding");
      else if (hadCall) { afterRateRef.current = () => navigate(`/chat/${matchId}`); setPhase("ended"); }
      else navigate(`/chat/${matchId}`);
    } else {
      setPhase("premium");
    }
  }, [matchId, stopStream, user, needsDecision, navigate]);

  useEffect(() => {
    if (phase !== "incall") return;
    const tick = setInterval(() => {
      if (!endTimeRef.current || pausedRef.current) return;
      const left = Math.max(0, Math.ceil((endTimeRef.current - Date.now()) / 1000));
      setRemaining(left);
      if (left <= 0) { clearInterval(tick); finishCall(); }
    }, 200);
    return () => clearInterval(tick);
  }, [phase, finishCall]);

  // Offer voice-only once when the connection turns red
  useEffect(() => {
    if (phase === "incall" && netState === "bad" && !audioOnly && !promptedRef.current) {
      promptedRef.current = true;
      setVoicePrompt(true);
    }
  }, [netState, phase, audioOnly]);

  const applyAudioOnly = (on) => {
    setAudioOnly(on); audioOnlyRef.current = on; setVoicePrompt(false);
    const call = dailyRef.current;
    if (dailyMode.current && call) {
      call.setLocalVideo(!on);
      Object.values(call.participants()).filter((p) => !p.local)
        .forEach((p) => call.updateParticipant(p.session_id, { setSubscribedTracks: { audio: true, video: !on } }));
    } else {
      const t = streamRef.current?.getVideoTracks()[0];
      if (t) t.enabled = !on;
    }
    setCamOn(!on);
    if (on) toast.success("Voice only — video paused to keep audio smooth");
  };

  const closeRating = () => {
    setRateOpen(false);
    const next = afterRateRef.current; afterRateRef.current = null;
    next?.();
  };

  const toggleMic = () => {
    if (dailyMode.current && dailyRef.current) {
      const next = !micOn;
      dailyRef.current.setLocalAudio(next);
      setMicOn(next);
      return;
    }
    const track = streamRef.current?.getAudioTracks()[0];
    if (track) { track.enabled = !track.enabled; setMicOn(track.enabled); }
  };
  const toggleCam = () => {
    if (dailyMode.current && dailyRef.current) {
      const next = !camOn;
      dailyRef.current.setLocalVideo(next);
      setCamOn(next);
      return;
    }
    const track = streamRef.current?.getVideoTracks()[0];
    if (track) { track.enabled = !track.enabled; setCamOn(track.enabled); }
  };

  const endEarly = () => {
    if (waitingStart) { syncPresence(false); setWaitingStart(false); }
    if (!needsDecision && !user?.premium && !FREE_PERIOD) { stopStream(); setPhase("premium"); return; }
    finishCall();
  };

  const requestRematch = async () => {
    try {
      const m = (await api.post(`/matches/${matchId}/rematch`)).data;
      setRematchInfo({ count: m.rematch_count ?? 0, max: m.rematch_max ?? 2 });
      stopStream();
      setRemaining(duration);
      setPhase("lobby");
      toast.success(`Another ${duration} seconds coming up`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "No rematch available");
    }
  };

  const sendNudge = async () => {
    try {
      const { data } = await api.post(`/matches/${matchId}/nudge`);
      if (data.reopened) {
        setRematchInfo({ count: data.match.rematch_count ?? 0, max: data.match.rematch_max ?? 2 });
        stopStream();
        setRemaining(DURATION);
        setPhase("lobby");
        toast.success("They're in — let's go again!");
      } else {
        toast.success(`Nudge sent to ${partner?.name} 💌`);
        navigate("/matches");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send nudge");
    }
  };

  const acceptNudge = async () => {
    try {
      const { data } = await api.post(`/matches/${matchId}/nudge/respond`, { accept: true });
      setRematchInfo({ count: data.match.rematch_count ?? 0, max: data.match.rematch_max ?? 2 });
      setNudgeIncoming(false);
      stopStream(); setRemaining(DURATION); setPhase("lobby");
      toast.success("Let's go again!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not accept");
    }
  };

  const declineNudge = async () => {
    try {
      await api.post(`/matches/${matchId}/nudge/respond`, { accept: false });
      toast.success("No worries — on to the next");
      navigate("/matches");
    } catch (e) {
      toast.error("Could not decline");
    }
  };

  const decide = async (decision) => {
    setDeciding(true);
    try {
      const m = (await api.post(`/matches/${matchId}/decision`, { decision })).data;
      if (m.status === "confirmed") setPhase("confirmed");
      else if (m.status === "closed") setPhase("closed");
      else { setPhase("waiting"); toast.success("Decision saved — waiting for them"); }
    } catch { toast.error("Could not save decision"); }
    finally { setDeciding(false); }
  };

  const urgent = remaining <= 15;
  const ringAgain = async () => {
    try { await api.post(`/matches/${matchId}/call/ring`); setInviteStatus("ringing"); } catch {}
  };
  const ratingOverlay = rateOpen && (
    <div data-testid="rate-overlay" className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-center justify-center px-5">
      <RateCallCard matchId={matchId} onDone={closeRating}
        meta={{ net_state: worstNetRef.current, audio_only: audioOnly, reconnects: reconnectsRef.current }} />
    </div>
  );

  if (phase === "loading") return <div className="min-h-screen flex items-center justify-center"><div className="h-10 w-10 rounded-full border-2 border-amber-400/30 border-t-amber-400 animate-spin" /></div>;

  if (phase === "ended") {
    return (
      <div className="min-h-screen flex items-center justify-center px-5">
        {ratingOverlay}
        <Button data-testid="ended-back-to-chat" onClick={() => navigate(`/chat/${matchId}`)} className="btn-gold rounded-full h-12 px-8"><MessageCircle className="h-4 w-4 mr-2" /> Back to chat</Button>
      </div>
    );
  }

  // Result screens
  if (phase === "confirmed" || phase === "closed" || phase === "waiting") {
    return (
      <div className="min-h-screen flex items-center justify-center px-5">
        <div className="glass rounded-3xl p-8 max-w-sm w-full text-center pop">
          {phase === "confirmed" && <>
            <div className="h-16 w-16 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-4"><Sparkles className="h-8 w-8 text-amber-400" /></div>
            <h2 className="font-display font-black text-3xl gold-text">You're on!</h2>
            <p className="text-sm text-emerald-100/70 mt-2">You both said yes to {event?.title}. Chat is unlocked — go plan your meet-up.</p>
            <Button data-testid="go-to-chat" onClick={() => navigate(`/chat/${matchId}`)} className="w-full btn-gold rounded-full h-12 mt-6"><MessageCircle className="h-4 w-4 mr-2" /> Open chat</Button>
          </>}
          {phase === "waiting" && <>
            <div className="h-16 w-16 rounded-full bg-amber-500/20 flex items-center justify-center mx-auto mb-4"><Heart className="h-8 w-8 text-pink-400" /></div>
            <h2 className="font-display font-black text-2xl">Decision saved</h2>
            <p className="text-sm text-emerald-100/70 mt-2">We'll let you know when {partner?.name} decides too.</p>
            <Button onClick={() => navigate("/matches")} className="w-full btn-gold rounded-full h-12 mt-6">Back to matches</Button>
          </>}
          {phase === "closed" && <>
            <div className="h-16 w-16 rounded-full bg-white/10 flex items-center justify-center mx-auto mb-4"><X className="h-8 w-8 text-emerald-100/60" /></div>
            <h2 className="font-display font-black text-2xl">No spark this time</h2>
            <p className="text-sm text-emerald-100/70 mt-2">This match has closed quietly. Plenty more events and people to discover.</p>
            {canRematch && <Button data-testid="closed-rematch" onClick={sendNudge} variant="outline" className="w-full rounded-full h-12 mt-6 border-amber-500/30 text-amber-300"><RotateCcw className="h-4 w-4 mr-2" /> Nudge them for a rematch</Button>}
            <Button data-testid="back-to-discover" onClick={() => navigate("/discover")} className="w-full btn-gold rounded-full h-12 mt-3">Find another event</Button>
          </>}
        </div>
      </div>
    );
  }

  // Premium upsell overlay (shown when the 60s timer ends)
  if (phase === "premium") {
    return (
      <div className="min-h-screen flex items-center justify-center px-5 bg-[#070d09]">
        {ratingOverlay}
        <div className="glass rounded-3xl p-8 max-w-sm w-full text-center pop border-amber-500/30" data-testid="premium-overlay">
          <div className="h-16 w-16 rounded-full bg-amber-500/20 flex items-center justify-center mx-auto mb-4"><Crown className="h-8 w-8 text-amber-400" /></div>
          <h2 className="font-display font-black text-2xl">Time's up — but the vibe isn't!</h2>
          <p className="text-sm text-slate-300 mt-3 leading-relaxed">Want to keep the vibe going? Subscribe to <span className="gold-text font-semibold">premium</span> for unlimited video calls and extra time limits!</p>
          <Button data-testid="upgrade-now" onClick={() => navigate("/premium")} className="w-full btn-gold rounded-full h-12 mt-6">
            <Crown className="h-4 w-4 mr-2" /> Upgrade Now
          </Button>
          {needsDecision ? (
            <Button data-testid="premium-continue" onClick={() => setPhase("deciding")} variant="ghost" className="w-full rounded-full h-11 mt-2 text-slate-300 hover:text-white hover:bg-white/5">Continue to your decision</Button>
          ) : (
            <Button data-testid="premium-continue" onClick={() => navigate(`/chat/${matchId}`)} variant="ghost" className="w-full rounded-full h-11 mt-2 text-slate-300 hover:text-white hover:bg-white/5">Back to chat</Button>
          )}
        </div>
      </div>
    );
  }

  // Decision screen — blurred video backdrop + dual "Lock it In" / "Next card" overlay
  if (phase === "deciding") {
    return (
      <div className="min-h-screen relative flex items-center justify-center px-5 overflow-hidden">
        {ratingOverlay}
        <img src={imgUrl(partner?.photos?.[0] || partner?.picture)} alt="" className="absolute inset-0 h-full w-full object-cover blur-2xl scale-110 opacity-40" />
        <div className="absolute inset-0 bg-[#070d09]/75" />
        <div className="glass rounded-3xl p-8 max-w-sm w-full text-center fade-up relative" data-testid="decision-overlay">
          <img src={imgUrl(partner?.photos?.[0] || partner?.picture)} alt={partner?.name} className="h-24 w-24 rounded-full object-cover mx-auto ring-4 ring-amber-400/40" />
          <h2 className="font-display font-black text-2xl mt-5">Time's up — lock it in?</h2>
          <p className="text-sm text-emerald-100/60 mt-2">Private &amp; honest. A chat for {event?.title} only opens if you both tap "Lock it In".</p>
          <div className="grid grid-cols-2 gap-4 mt-7">
            <Button data-testid="decide-no" disabled={deciding} onClick={() => decide("no")} variant="outline" className="rounded-2xl h-24 flex-col gap-2 border-white/15 hover:bg-white/5">
              <ThumbsDown className="h-7 w-7 text-emerald-100/70" /><span className="font-semibold">Next card</span>
            </Button>
            <Button data-testid="decide-yes" disabled={deciding} onClick={() => decide("yes")} className="btn-gold rounded-2xl h-24 flex-col gap-2">
              <ThumbsUp className="h-7 w-7" /><span className="font-semibold">Lock it In</span>
            </Button>
          </div>
          {canRematch && (
            <button data-testid="request-rematch" onClick={requestRematch} className="mt-5 text-sm text-amber-300 hover:text-amber-200 inline-flex items-center gap-2">
              <RotateCcw className="h-4 w-4" /> Not sure yet? Request another {duration} seconds
            </button>
          )}
          {!user?.premium && !FREE_PERIOD && (
            <div className="mt-4">
              <button data-testid="call-premium-nudge" onClick={() => navigate("/premium")}
                className="text-xs text-amber-300/90 hover:text-amber-200 inline-flex items-center gap-1.5 transition-colors">
                <Crown className="h-3.5 w-3.5" /> Loved it? Go Premium for 90-second calls →
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Incoming rematch nudge — accept or decline
  if (phase === "nudge") {
    return (
      <div className="min-h-screen flex items-center justify-center px-5">
        <div className="glass rounded-3xl p-8 max-w-sm w-full text-center fade-up">
          <div className="h-14 w-14 rounded-full bg-amber-500/20 flex items-center justify-center mx-auto mb-4"><RotateCcw className="h-7 w-7 text-amber-400" /></div>
          <img src={imgUrl(partner?.photos?.[0] || partner?.picture)} alt={partner?.name} className="h-24 w-24 rounded-full object-cover mx-auto ring-4 ring-pink-400/40" />
          <h2 className="font-display font-black text-2xl mt-5">{partner?.name} wants a rematch 🔁</h2>
          <p className="text-sm text-emerald-100/70 mt-2">They'd love another 60-second vibe check before {event?.title}. Up for it?</p>
          <div className="grid grid-cols-2 gap-4 mt-7">
            <Button data-testid="nudge-decline" onClick={declineNudge} variant="outline" className="rounded-2xl h-14 border-white/15 hover:bg-white/5">No thanks</Button>
            <Button data-testid="nudge-accept" onClick={acceptNudge} className="btn-pink rounded-2xl h-14">Yes, let's go</Button>
          </div>
        </div>
      </div>
    );
  }

  // Lobby — camera & mic check
  if (phase === "lobby") {
    return (
      <div className="min-h-screen flex items-center justify-center px-5 py-10">
        <MediaPermissionDialog error={mediaError} onRetry={retryMedia} onClose={() => setMediaError(null)} />
        <div className="glass rounded-3xl p-6 sm:p-8 max-w-3xl w-full fade-up">
          <div className="text-center">
            <span className="chip inline-block text-[11px] font-semibold uppercase tracking-widest px-3 py-1 rounded-full">The gatekeeper</span>
            <h2 className="font-display font-black text-3xl sm:text-4xl mt-4">Ready for your {duration}-second vibe check?</h2>
            <p className="text-sm text-emerald-100/70 mt-2 max-w-lg mx-auto">A quick, low-pressure video call with <span className="text-amber-300 font-semibold">{partner?.name}</span> before you plan {event?.title}. Let's check your camera & mic first.</p>
          </div>

          <div className="grid sm:grid-cols-2 gap-5 mt-7 items-center">
            {/* Preview */}
            <div className="relative rounded-2xl overflow-hidden border border-amber-500/20 bg-black aspect-[4/3]">
              {camError ? (
                <div className="h-full w-full flex flex-col items-center justify-center text-center p-5 gap-2">
                  <VideoOff className="h-7 w-7 text-pink-300" />
                  <span className="text-xs text-emerald-100/60">Camera blocked. Allow camera & mic access in your browser to preview — you can still join.</span>
                </div>
              ) : (
                <video data-testid="lobby-preview" ref={videoRef} autoPlay playsInline muted className="h-full w-full object-cover scale-x-[-1]" />
              )}
              {!camOn && !camError && <div className="absolute inset-0 bg-black/80 flex items-center justify-center"><VideoOff className="h-7 w-7 text-emerald-100/40" /></div>}
              <div className="absolute bottom-3 left-3 right-3 flex items-center gap-3">
                <button data-testid="lobby-mic" onClick={toggleMic} className={`h-10 w-10 rounded-full flex items-center justify-center ${micOn ? "bg-white/15 text-white" : "bg-pink-500/40 text-pink-100"}`}>
                  {micOn ? <Mic className="h-4 w-4" /> : <MicOff className="h-4 w-4" />}
                </button>
                <button data-testid="lobby-cam" onClick={toggleCam} className={`h-10 w-10 rounded-full flex items-center justify-center ${camOn ? "bg-white/15 text-white" : "bg-pink-500/40 text-pink-100"}`}>
                  {camOn ? <Video className="h-4 w-4" /> : <VideoOff className="h-4 w-4" />}
                </button>
                {/* mic level meter */}
                <div className="flex-1 h-2 rounded-full bg-white/15 overflow-hidden">
                  <div className="h-full bg-emerald-400 rounded-full transition-[width] duration-100" style={{ width: `${Math.round(micLevel * 100)}%` }} />
                </div>
              </div>
            </div>

            {/* Partner + status */}
            <div className="text-center sm:text-left">
              <div className="flex items-center gap-3 justify-center sm:justify-start">
                <img src={imgUrl(partner?.photos?.[0] || partner?.picture)} alt={partner?.name} className="h-16 w-16 rounded-full object-cover ring-2 ring-pink-400/40" />
                <div>
                  <p className="font-display font-bold text-lg flex items-center gap-2">{partner?.name} {partner?.verified && <VerifiedBadge size="xs" />} {partner?.premium && <PremiumBadge size="xs" showLabel={false} />}</p>
                  <p className="text-xs text-emerald-100/55">is ready to vibe</p>
                </div>
              </div>
              <div className="space-y-2 mt-5 text-sm">
                <div className="flex items-center gap-2 justify-center sm:justify-start">
                  <span className={`h-2.5 w-2.5 rounded-full ${!camError && camOn ? "bg-emerald-400" : "bg-pink-400"}`} />
                  <span className="text-emerald-100/70">{!camError && camOn ? "Camera ready" : "Camera off"}</span>
                </div>
                <div className="flex items-center gap-2 justify-center sm:justify-start">
                  <span className={`h-2.5 w-2.5 rounded-full ${!camError && micOn ? "bg-emerald-400" : "bg-pink-400"}`} />
                  <span className="text-emerald-100/70">{!camError && micOn ? (micLevel > 0.05 ? "Mic picking you up" : "Mic ready — say something") : "Mic off"}</span>
                </div>
              </div>
              <NetworkCheck />
              {!localStorage.getItem(DEVICE_KEYS.tested) ? (
                <div data-testid="first-call-prompt" className="mt-4 rounded-2xl border border-sky-400/30 bg-sky-400/5 p-3.5 text-left">
                  <p className="text-sm font-semibold">First vibe check?</p>
                  <p className="text-xs text-emerald-100/60">Take 20 seconds to test your camera, mic and speaker so nothing goes wrong.</p>
                  <div className="flex gap-2 mt-2.5">
                    <button data-testid="first-call-test-btn" onClick={() => { stopStream(); navigate(`/setup-test?next=/call/${matchId}`); }} className="btn-gold rounded-full h-8 px-4 text-xs font-semibold">Test my setup</button>
                    <button data-testid="first-call-skip-btn" onClick={() => { localStorage.setItem(DEVICE_KEYS.tested, "skipped"); setSkipTick((t) => t + 1); }} className="rounded-full h-8 px-4 text-xs border border-white/15 text-white/70">Skip</button>
                  </div>
                </div>
              ) : (
                <button data-testid="lobby-setup-test-link" onClick={() => { stopStream(); navigate(`/setup-test?next=/call/${matchId}`); }}
                  className="text-xs text-sky-300 hover:text-sky-200 mt-3 block mx-auto sm:mx-0">Test camera & mic</button>
              )}
              <Button data-testid="begin-call-btn" onClick={() => setPhase("countdown")} className="w-full btn-gold rounded-full h-12 mt-6">
                <Video className="h-4 w-4 mr-2" /> I'm ready
              </Button>
              <button onClick={() => { stopStream(); navigate("/matches"); }} className="text-xs text-emerald-100/50 mt-3 hover:text-emerald-100/80 block mx-auto sm:mx-0">Not now</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // 3-2-1 pre-call countdown
  if (phase === "limit") {
    return (
      <div data-testid="call-limit-screen" className="min-h-screen flex items-center justify-center px-5">
        <div className="glass rounded-3xl p-8 max-w-sm w-full text-center">
          <Video className="h-10 w-10 text-pink-300 mx-auto" />
          <h2 className="font-display font-black text-2xl mt-3">Daily vibe checks used</h2>
          <p className="text-sm text-slate-300 mt-2">Your free allowance resets at midnight (London time).</p>
          <Button data-testid="limit-see-options" onClick={() => api.get("/usage").then(({ data }) => openUpgradeSheet({ reason: "calls", message: data.limit_message, usage: data }))} className="w-full btn-gold rounded-full h-12 mt-6">See options</Button>
          <Button data-testid="limit-back" variant="outline" onClick={() => navigate("/discover")} className="w-full rounded-full h-12 mt-3 border-white/15">Back to Discover</Button>
        </div>
      </div>
    );
  }

  if (phase === "countdown") {
    return (
      <div className="min-h-screen flex items-center justify-center px-5 bg-[#070d09]">
        <div className="text-center">
          <p className="text-sm uppercase tracking-[0.3em] text-amber-400/80 mb-6">Connecting with {partner?.name}</p>
          <div key={preCount} className="pop font-display font-black text-[7rem] leading-none gold-text" data-testid="precall-countdown">{preCount}</div>
          <p className="text-emerald-100/60 mt-6">Get comfy — your {duration} seconds start now.</p>
        </div>
      </div>
    );
  }

  // In-call: WhatsApp-style — partner full-screen, self floating thumbnail, timer top-left
  const shown = waitingStart ? duration : remaining;
  const clock = `${Math.floor(shown / 60)}:${String(shown % 60).padStart(2, "0")}`;
  return (
    <div data-testid="call-screen" className="fixed inset-0 h-[100dvh] overflow-hidden bg-black">
      {dailyMode.current ? (
        <video ref={remoteVideoRef} autoPlay playsInline className="absolute inset-0 h-full w-full object-cover bg-black" data-testid="remote-video" />
      ) : null}
      {(!dailyMode.current || !remoteP || remoteP?.tracks?.video?.state !== "playable") && (
        <img src={imgUrl(partner?.photos?.[0] || partner?.picture)} alt={partner?.name}
          className={`absolute inset-0 h-full w-full object-cover ${dailyMode.current ? "opacity-50 blur-[2px]" : ""}`} />
      )}
      <div className="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-black/70 to-transparent pointer-events-none" />
      <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-black/80 to-transparent pointer-events-none" />

      {/* Top-left: timer + name */}
      <div className="absolute left-4 z-20" style={{ top: "max(1rem, env(safe-area-inset-top))" }}>
        <div className="flex items-center gap-2">
          <span data-testid="countdown" className={`font-mono-doek font-bold text-lg tabular-nums ${waitingStart ? "text-white/60" : urgent ? "text-pink-400" : "text-white"}`}>{clock}</span>
          {waitingStart && <span data-testid="countdown-waiting" className="text-[11px] text-white/60">starts together</span>}
          <span data-testid="net-quality" title={`Connection: ${netState}`} className={`h-2 w-2 rounded-full ${netState === "good" ? "bg-emerald-400" : netState === "warning" ? "bg-amber-400" : "bg-pink-500"}`} />
        </div>
        <div className="flex items-center gap-1.5 mt-0.5">
          <span className="text-base font-semibold drop-shadow">{partner?.name}</span>
          {partner?.verified && <VerifiedBadge size="xs" />}
          {partner?.premium && <PremiumBadge size="xs" showLabel={false} />}
          {partner?.is_demo && <span className="text-[10px] text-white/50">(demo)</span>}
        </div>
        {dailyMode.current && !remoteP && !partner?.is_demo && <p className="text-[11px] text-amber-200/80">connecting…</p>}
        {netState !== "good" && <p data-testid="net-quality-warning" className="text-[11px] text-amber-200/90">Weak connection — lowering video to keep audio clear</p>}
      </div>

      {/* Self floating thumbnail */}
      <div data-testid="self-tile" className="absolute right-4 z-20 h-36 w-24 sm:h-48 sm:w-36 rounded-2xl overflow-hidden border border-white/20 shadow-2xl bg-neutral-900" style={{ top: "max(1rem, env(safe-area-inset-top))" }}>
        {dailyMode.current ? (
          <video ref={localVideoRef} autoPlay playsInline muted className="h-full w-full object-cover scale-x-[-1]" data-testid="self-video" />
        ) : camError ? (
          <div className="h-full w-full flex items-center justify-center text-center p-2"><span className="text-[10px] text-white/50">Camera off</span></div>
        ) : (
          <video ref={videoRef} autoPlay playsInline muted className="h-full w-full object-cover scale-x-[-1]" />
        )}
        {!camOn && <div className="absolute inset-0 bg-black/80 flex items-center justify-center"><VideoOff className="h-6 w-6 text-white/40" /></div>}
      </div>

      {dailyMode.current && !remoteP && !partner?.is_demo && (
        <div data-testid="waiting-for-partner" className="absolute inset-x-0 bottom-32 flex justify-center z-10 px-4">
          <div data-testid="invite-status" className="glass rounded-full px-5 py-2.5 text-sm flex items-center gap-2 text-center">
            <span className={`h-2 w-2 rounded-full shrink-0 ${inviteStatus === "missed" ? "bg-pink-400" : "bg-amber-400 animate-pulse"}`} />
            {inviteStatus === "missed" ? <>No answer from {partner?.name} <button data-testid="ring-again" onClick={ringAgain} className="ml-1 font-semibold text-amber-300 underline">Ring again</button></>
              : inviteStatus === "ringing" ? `Ringing ${partner?.name}… your ${duration}s timer starts when you're both here`
              : `Waiting for ${partner?.name}… your ${duration}s timer starts when you're both here`}
          </div>
        </div>
      )}
      {dailyMode.current && remoteP && (
        <PartnerMediaBanner name={partner?.name}
          video={remoteP?.tracks?.video?.state === "blocked"} audio={remoteP?.tracks?.audio?.state === "blocked"} />
      )}
      {remoteMuted && remoteP && (
        <button data-testid="unmute-remote" onClick={unmuteRemote} className="absolute bottom-32 left-1/2 -translate-x-1/2 z-30 btn-gold rounded-full px-5 py-2.5 text-sm font-semibold flex items-center gap-2">
          <Mic className="h-4 w-4" /> Tap to hear {partner?.name}
        </button>
      )}
      <MediaPermissionDialog error={mediaError} onRetry={retryMedia} onClose={() => setMediaError(null)} />

      {(paused || selfReconnecting) && (
        <div data-testid="reconnect-overlay" className="absolute inset-0 z-30 flex items-center justify-center bg-black/60 backdrop-blur-sm px-6 pointer-events-none">
          <div className="glass rounded-3xl px-6 py-5 text-center max-w-xs">
            <WifiOff className="h-8 w-8 text-amber-300 mx-auto" />
            <p className="font-display font-bold text-lg mt-2">{selfReconnecting ? "You're reconnecting…" : `${partner?.name}'s connection dropped`}</p>
            <p className="text-sm text-slate-300 mt-1">Timer paused{pauseLeft != null ? ` · waiting up to ${pauseLeft}s` : ""} — we'll rejoin automatically.</p>
          </div>
        </div>
      )}

      {audioOnly && (
        <div data-testid="audio-only-label" className="absolute inset-x-0 top-1/2 -translate-y-1/2 z-10 flex flex-col items-center gap-2 pointer-events-none">
          <AudioLines className="h-10 w-10 text-white/80 animate-pulse" />
          <span className="text-sm text-white/80">Voice only</span>
        </div>
      )}

      {voicePrompt && !audioOnly && (
        <div data-testid="voice-only-prompt" className="absolute inset-x-4 bottom-28 z-30 flex justify-center">
          <div className="glass rounded-2xl px-4 py-3 flex items-center gap-3 max-w-sm border-pink-500/40">
            <WifiOff className="h-5 w-5 text-pink-300 shrink-0" />
            <p className="text-sm flex-1">Signal is very weak. Switch to voice only?</p>
            <button data-testid="voice-only-yes" onClick={() => applyAudioOnly(true)} className="btn-gold rounded-full px-3 py-1.5 text-xs font-semibold">Switch</button>
            <button data-testid="voice-only-dismiss" onClick={() => setVoicePrompt(false)} className="text-xs text-slate-300 hover:text-white">Not now</button>
          </div>
        </div>
      )}

      {/* Floating controls */}
      <div className="absolute inset-x-0 bottom-0 z-40 flex justify-center gap-3 sm:gap-5 px-4" style={{ paddingBottom: "max(1.5rem, env(safe-area-inset-bottom))" }}>
        <button data-testid="toggle-mic" onClick={toggleMic} className={`h-14 w-14 rounded-full flex items-center justify-center backdrop-blur-md transition-colors ${micOn ? "bg-white/15 text-white" : "bg-white text-black"}`}>
          {micOn ? <Mic className="h-6 w-6" /> : <MicOff className="h-6 w-6" />}
        </button>
        <button data-testid="toggle-cam" onClick={toggleCam} className={`h-14 w-14 rounded-full flex items-center justify-center backdrop-blur-md transition-colors ${camOn ? "bg-white/15 text-white" : "bg-white text-black"}`}>
          {camOn ? <Video className="h-6 w-6" /> : <VideoOff className="h-6 w-6" />}
        </button>
        <button data-testid="toggle-audio-only" onClick={() => applyAudioOnly(!audioOnly)} title="Voice only"
          className={`h-14 w-14 rounded-full flex items-center justify-center backdrop-blur-md transition-colors ${audioOnly ? "bg-white text-black" : "bg-white/15 text-white"}`}>
          <AudioLines className="h-6 w-6" />
        </button>
        <ReportDialog reportedId={partner?.user_id} matchId={matchId}
          trigger={<button data-testid="call-report" className="h-14 w-14 rounded-full bg-white/15 backdrop-blur-md text-white flex items-center justify-center"><Flag className="h-5 w-5" /></button>} />
        <button data-testid="end-call" onClick={endEarly} className="h-14 w-14 rounded-full bg-red-500 text-white flex items-center justify-center shadow-lg hover:bg-red-600 transition-colors">
          <PhoneOff className="h-6 w-6" />
        </button>
      </div>
    </div>
  );
}
